import { wachtOpPartnerAntwoord } from './partnerconcept.js';
import { AFDOENING_VERB, afdoeningVraagplanBlokkade } from './mail-afdoening.js';
/** Shared presentation contract. Only server-generated plans and stored decisions are evidence. */
export interface VraagActieBinding {
  sleutel: string;
  vraag_sleutel: string;
  generatie_id: string;
}
export interface MailVraag {
  sleutel: string;
  bron_message_id: string;
  citaat: string;
  vraag: string;
  benodigde_feiten: string[];
  ontbreekt: string[];
  afhandeling: 'antwoord' | 'actie' | 'handmatig';
  acties: string[];
  afhankelijk_van: string[];
  resterende_acties: number;
  /** Server-owned identities of proposals without a card; retained across model omissions. */
  uitgestelde_acties?: string[];
}
export interface MailVraagplan {
  versie: 1;
  generatie_id: string;
  bron_message_id: string;
  bron_fingerprint: string;
  recept: { id: 'mail-vragen'; revisie: 1 };
  bronnen: Array<{ message_id: string; tekens: number; gelezen: number; citaten_afgezonderd: boolean;
    /** Server snapshot from migration 190, absent on older plans. Never supplied by the model. */
    inhoud_hash?: string; is_incoming?: boolean; message_date?: string; onderwerp?: string }>;
  bijlagen: Array<{ id: string; message_id: string; naam: string; gelezen: false }>;
  beperkingen: string[];
  model_volledig: boolean;
  vragen: MailVraag[];
}
export type VraagStatus = 'informatie_nodig' | 'besluit_nodig' | 'uitvoering_bezig' | 'uitgevoerd'
  | 'afgewezen' | 'mislukt' | 'overgedragen' | 'antwoord_voorbereid';
type Actie = { verb: string; vraag?: VraagActieBinding; uitvoering?: unknown;
  uitkomst?: { besluit: string; ok?: boolean; partnerconcept?: unknown } };

export function leesVraagplan(raw: unknown): MailVraagplan | null {
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as MailVraagplan;
  if (p.versie !== 1 || typeof p.generatie_id !== 'string' || typeof p.bron_message_id !== 'string'
    || !/^[a-f0-9]{64}$/.test(p.bron_fingerprint) || !Array.isArray(p.vragen)
    || !Array.isArray(p.beperkingen) || !Array.isArray(p.bronnen) || !Array.isArray(p.bijlagen)) return null;
  if (p.vragen.some(v => !v || typeof v.sleutel !== 'string' || typeof v.vraag !== 'string'
    || typeof v.citaat !== 'string' || typeof v.bron_message_id !== 'string'
    || !['antwoord', 'actie', 'handmatig'].includes(v.afhandeling)
    || !Number.isInteger(v.resterende_acties) || v.resterende_acties < 0
    || (v.uitgestelde_acties !== undefined && (!Array.isArray(v.uitgestelde_acties) || v.uitgestelde_acties.some(s => typeof s !== 'string')))
    || [v.acties, v.afhankelijk_van, v.benodigde_feiten, v.ontbreekt].some(a => !Array.isArray(a) || a.some(s => typeof s !== 'string')))) return null;
  return p;
}

/** A saved callback/assessment is a handover, not resolution of the customer's question. */
export function vraagStatus(vraag: MailVraag, acties: Actie[]): VraagStatus {
  const gekoppeld = acties.filter(a => a.vraag && vraag.acties.includes(a.vraag.sleutel));
  if (gekoppeld.some(a => a.uitvoering && !a.uitkomst)) return 'uitvoering_bezig';
  if (gekoppeld.some(a => a.uitkomst?.besluit === 'akkoord' && a.uitkomst.ok !== true)) return 'mislukt';
  if (gekoppeld.some(a => !a.uitkomst)) return 'besluit_nodig';
  if (gekoppeld.some(a => a.uitkomst?.besluit === 'afgewezen')) return 'afgewezen';
  if (gekoppeld.some(wachtOpPartnerAntwoord)) return 'informatie_nodig';
  if (gekoppeld.some(a => a.uitkomst?.ok === true && ['create_task', 'vraag_klachtbeoordeling', 'registreer_klacht'].includes(a.verb))) return 'overgedragen';
  if (vraag.ontbreekt.length || vraag.resterende_acties || vraag.acties.length !== gekoppeld.length || vraag.afhandeling === 'handmatig') return 'informatie_nodig';
  if (gekoppeld.length && gekoppeld.every(a => a.uitkomst?.ok === true)) return 'uitgevoerd';
  return vraag.afhandeling === 'antwoord' ? 'antwoord_voorbereid' : 'informatie_nodig';
}

export function vraagActieBlokkade(plan: MailVraagplan, acties: Actie[], sleutel: string): string | null {
  if (acties.some(a => a.vraag?.sleutel === sleutel && a.verb === AFDOENING_VERB)) {
    const blokkade = afdoeningVraagplanBlokkade(plan, acties);
    if (blokkade) return blokkade;
  }
  const vraag = plan.vragen.find(v => v.acties.includes(sleutel));
  if (!vraag) return 'De actie is niet aan een herkende vraag gekoppeld. Maak een nieuw voorstel.';
  if (vraag.ontbreekt.length) return `Eerst nodig: ${vraag.ontbreekt.join('; ')}`;
  for (const id of vraag.afhankelijk_van) {
    const voorganger = plan.vragen.find(v => v.sleutel === id);
    // A drafted answer or task is not proof that a prerequisite was executed.
    if (!voorganger || vraagStatus(voorganger, acties) !== 'uitgevoerd') {
      return `Eerst afhandelen en controleren: ${voorganger?.vraag || 'onbekende voorafgaande vraag'}`;
    }
  }
  return null;
}

export const VRAAG_STATUS_LABELS: Record<VraagStatus, string> = {
  informatie_nodig: 'Nog informatie of handwerk nodig', besluit_nodig: 'Besluit nodig', uitvoering_bezig: 'Uitvoering bezig',
  uitgevoerd: 'Uitgevoerd', afgewezen: 'Afgewezen, vraag blijft open', mislukt: 'Uitvoering niet bevestigd',
  overgedragen: 'Overgedragen, nog niet opgelost', antwoord_voorbereid: 'Antwoord voorbereid',
};
